import React, { useMemo } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Preloader from "~/components/Preloader";
import CatalogForm, {
  ICatalogCreateForm,
} from "~/pages/Editor/EditorDocumentSection/modals/CatalogForm";
import { useGetLocationData } from "~/hooks/locationData";
import { TCatalog_identifier } from "~/model/Instances/TCatalog";
import {
  convertDataToRequestCreateCatalog_CatalogModal,
  getValuesFromInstance_CatalogModal,
} from "~/pages/Editor/EditorDocumentSection/utils/utils";
import {
  Section,
  useCreateCatalogMutation,
  useEditCatalogMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

const formId = "CatalogForm";

export interface ICatalogCreateSignature {
  name: string;
  parentId?: string;
}

interface CatalogModal {
  isOpen: boolean;
  isEdit: boolean;
  isParentData: boolean;
  handleCloseModal: () => void;
  selectedCatalog?: TCatalog_identifier;
  parentCatalog?: TCatalog_identifier;
  reFetchTable?: (id?: string) => void;
  loading?: boolean;
}

export const CatalogModal = (props: CatalogModal) => {
  const {
    isOpen,
    isEdit,
    isParentData,
    handleCloseModal,
    selectedCatalog,
    parentCatalog,
    reFetchTable,
    loading,
  } = props;
  const params = useParams<{ id: string }>();
  const { id } = params;
  const history = useHistory();
  const { t } = useTranslation();
  const { currentId } = useGetLocationData();

  const { notifySuccess } = useNotify();

  const onCompleted = (res) => {
    notifySuccess(
      isEdit
        ? t("documentLib.notification.editSuccess")
        : t("documentLib.notification.createSuccess"),
    );
    try {
      const updatedCatalogInstance = isEdit
        ? (get(res, "DocumentSetter.editSection", {}) as Section)
        : (get(res, "DocumentSetter.createSection", {}) as Section);

      if (isEdit && !!parentCatalog) {
        updatedCatalogInstance.parentId !== id &&
          history.push(`/editor/doc/${updatedCatalogInstance.parentId}/`);
      }

      handleCloseModal();
      reFetchTable(updatedCatalogInstance.parentId);
    } catch (e) {
      handleCloseModal();
      reFetchTable(id);
    }
  };

  const [createCatalog] = useCreateCatalogMutation({
    onCompleted,
  });
  const [editCatalog] = useEditCatalogMutation({
    onCompleted,
  });

  const onSubmit = (data: ICatalogCreateForm) => {
    isEdit
      ? editCatalog({
          variables: {
            id: currentId,
            ...convertDataToRequestCreateCatalog_CatalogModal(data),
          },
        })
      : createCatalog({
          variables: convertDataToRequestCreateCatalog_CatalogModal(data),
        });
  };

  const title = useMemo(
    () =>
      selectedCatalog
        ? t("documentLib.catalogEdit")
        : t("documentLib.catalogCreate"),
    [selectedCatalog],
  );

  const defaultValue = useMemo(() => {
    return selectedCatalog
      ? getValuesFromInstance_CatalogModal(selectedCatalog, isParentData)
      : {
          name: "",
          parentId: { value: parentCatalog?.id, label: parentCatalog?.name },
        };
  }, [selectedCatalog, parentCatalog]);

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: formId,
        children: t("ui.save"),
      }}
    >
      {loading ? (
        <Preloader />
      ) : (
        <CatalogForm
          values={defaultValue}
          hasChild={!id}
          onSubmitHandler={onSubmit}
          formId={formId}
        />
      )}
    </Modal>
  );
};
